import { useEffect, useRef, useState } from 'react';
import {
  HOUR_PX, WEEKDAYS_SHORT, calendarOf, sameDay, minutesOfDay, formatTime,
  timedEventsOn, layoutSegments, layoutAllDayRow, dateKey, tzShort,
} from '../../lib/calendar';

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const GUTTER = 56;

// Hafta / Gün görünümü: saat ızgarası, tüm gün şeridi, şimdi çizgisi, çakışan etkinlikler yan yana
export const TimeGrid = ({ days, events, holidays, onSlotClick, onEventClick, selectedId }) => {
  const scrollRef = useRef(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  // Açılışta çalışma saatlerine kaydır (bugün görünüyorsa şimdinin 1 saat öncesine)
  const rangeKey = dateKey(days[0]) + days.length;
  useEffect(() => {
    if (!scrollRef.current) return;
    const todayVisible = days.some((d) => sameDay(d, new Date()));
    const targetHour = todayVisible ? Math.max(0, new Date().getHours() - 1) : 8;
    scrollRef.current.scrollTop = targetHour * HOUR_PX - 8;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);

  const { items: allDayItems, rows } = layoutAllDayRow(events, days);
  const holidayDays = days.map((d) => holidays?.[dateKey(d)]);
  const holidayRow = holidayDays.some(Boolean);

  const clickColumn = (e, day) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const minutes = Math.min(23 * 60 + 30, Math.max(0, Math.round(((e.clientY - rect.top) / HOUR_PX) * 2) * 30));
    onSlotClick(new Date(day.getFullYear(), day.getMonth(), day.getDate(), Math.floor(minutes / 60), minutes % 60));
  };

  const cols = `${GUTTER}px repeat(${days.length}, minmax(0, 1fr))`;

  return (
    <div className="flex-1 min-h-0 flex flex-col" data-testid="time-grid">
      {/* Gün başlıkları */}
      <div className="grid border-b border-border shrink-0" style={{ gridTemplateColumns: cols }}>
        <div className="text-[10px] text-muted-foreground flex items-end justify-end pr-2 pb-1">{tzShort()}</div>
        {days.map((d) => {
          const today = sameDay(d, now);
          return (
            <div key={dateKey(d)} className="py-2 text-center border-l border-border">
              <div className="text-xs text-muted-foreground">{WEEKDAYS_SHORT[(d.getDay() + 6) % 7]}</div>
              <div className={`mx-auto mt-0.5 h-8 w-8 rounded-full flex items-center justify-center text-base font-medium ${today ? 'bg-primary text-primary-foreground' : ''}`}>
                {d.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tüm gün + tatiller */}
      {(rows > 0 || holidayRow) && (
        <div className="grid border-b border-border shrink-0 py-1" style={{ gridTemplateColumns: cols }} data-testid="all-day-row">
          <div className="text-[10px] text-muted-foreground flex items-start justify-end pr-2 pt-1">Tüm gün</div>
          <div className="relative" style={{ gridColumn: `2 / span ${days.length}`, minHeight: Math.max(1, rows + (holidayRow ? 1 : 0)) * 24 }}>
            <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
              {days.map((d) => <div key={dateKey(d)} className="border-l border-border" />)}
            </div>
            {holidayRow && holidayDays.map((name, i) => name && (
              <div key={`h-${i}`} className="absolute top-0.5 px-1" style={{ left: `${(i / days.length) * 100}%`, width: `${100 / days.length}%` }}>
                <div className="h-5 truncate rounded px-1.5 text-[11px] leading-5 bg-secondary text-muted-foreground" title={name}>{name}</div>
              </div>
            ))}
            {allDayItems.map(({ event, startCol, endCol, row }) => (
              <button
                key={event.id}
                onClick={() => onEventClick(event)}
                className={`absolute h-5 truncate rounded border-l-[3px] px-1.5 text-left text-[11px] leading-5 font-medium ${calendarOf(event.color).box} ${selectedId === event.id ? 'ring-2 ring-primary' : ''}`}
                style={{
                  left: `calc(${(startCol / days.length) * 100}% + 2px)`,
                  width: `calc(${((endCol - startCol + 1) / days.length) * 100}% - 4px)`,
                  top: (row + (holidayRow ? 1 : 0)) * 24 + 2,
                }}
                title={event.title}
                data-testid={`event-${event.id}`}
              >
                {event.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Saat ızgarası */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
        <div className="relative grid" style={{ gridTemplateColumns: cols, height: 24 * HOUR_PX }}>
          <div className="relative">
            {HOURS.slice(1).map((h) => (
              <div key={h} className="absolute right-2 -translate-y-1/2 text-[11px] text-muted-foreground" style={{ top: h * HOUR_PX }}>
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>
          {days.map((d) => {
            const segments = layoutSegments(timedEventsOn(events, d));
            const today = sameDay(d, now);
            return (
              <div
                key={dateKey(d)}
                className="relative border-l border-border cursor-pointer"
                style={{ backgroundImage: `repeating-linear-gradient(to bottom, transparent 0, transparent ${HOUR_PX - 1}px, hsl(var(--border)) ${HOUR_PX - 1}px, hsl(var(--border)) ${HOUR_PX}px)` }}
                onClick={(e) => clickColumn(e, d)}
                data-testid={`day-col-${dateKey(d)}`}
              >
                {segments.map((seg) => {
                  const { event } = seg;
                  const height = ((seg.endMin - seg.startMin) * HOUR_PX) / 60;
                  return (
                    <button
                      key={`${event.id}-${seg.startMin}`}
                      onClick={(e) => { e.stopPropagation(); onEventClick(event); }}
                      className={`absolute overflow-hidden rounded-md border-l-[3px] px-1.5 py-0.5 text-left text-[11px] leading-tight hover:shadow-md transition-shadow ${calendarOf(event.color).box} ${selectedId === event.id ? 'ring-2 ring-primary z-10' : ''}`}
                      style={{
                        top: (seg.startMin * HOUR_PX) / 60,
                        height: Math.max(height - 1, 18),
                        left: `calc(${(seg.col / seg.cols) * 100}% + 1px)`,
                        width: `calc(${100 / seg.cols}% - 3px)`,
                      }}
                      title={`${event.title} (${formatTime(event.start)} – ${formatTime(event.end)})`}
                      data-testid={`event-${event.id}`}
                    >
                      <div className="font-semibold truncate">{event.title}</div>
                      {height >= 38 && <div className="opacity-80 truncate">{formatTime(event.start)} – {formatTime(event.end)}</div>}
                      {height >= 62 && event.location && <div className="opacity-70 truncate">{event.location}</div>}
                    </button>
                  );
                })}
                {today && (
                  <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top: (minutesOfDay(now) * HOUR_PX) / 60 }} data-testid="now-line">
                    <div className="h-px bg-primary" />
                    <div className="absolute -left-1 -top-[3px] h-[7px] w-[7px] rounded-full bg-primary" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TimeGrid;
